import React, { useMemo } from "react";
import countries from "i18n-iso-countries";
import {
  useWatch,
  type Control,
  type UseFormResetField,
  type UseFormSetFocus,
  type UseFormSetValue,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LetterIcon } from "@components/icons";
import { FormAddressInput } from "@components/inputs/form/FormAddressInput/FormAddressInput";
import { FormAutocomplete } from "@components/inputs/form/FormAutocomplete/FormAutocomplete";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormPhoneInput } from "@components/inputs/form/FormPhoneInput/FormPhoneInput";
import type { IContactInformation } from "@services/knowYourCustomer/types";
import { useCurrentLanguageCode } from "@utils/common";
import { COUNTRY_CODE } from "@/constants";

interface IContactInformationAddressFormProps {
  formFieldPrefix: "registrationInformation" | "placeOfResidence";
  availableCountries: IContactInformation["countryCodes"];
  control: Control<IContactInformation>;
  resetField: UseFormResetField<IContactInformation>;
  setFocus: UseFormSetFocus<IContactInformation>;
  setValue: UseFormSetValue<IContactInformation>;
}

export const ContactInformationAddressForm: React.FC<
  IContactInformationAddressFormProps
> = ({
  availableCountries,
  control,
  formFieldPrefix,
  resetField,
  setFocus,
  setValue,
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguageCode = useCurrentLanguageCode();

  const { countryOptions } = useMemo(() => {
    if (!availableCountries) {
      return { countryOptions: [] };
    }

    let countryOptions = (availableCountries ?? [])
      .map((country) => {
        const existsInTranslations = i18n.exists(`country.${country.name}`);

        const fallbackCountryName = existsInTranslations
          ? t(`country.${country.name}`)
          : country.name;

        const countryName =
          countries.getName(country.code, currentLanguageCode) ??
          fallbackCountryName;

        return {
          value: country.code,
          label: countryName,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    // move current country to top of the list
    const currentCountry = (availableCountries ?? []).find(
      (i) => i.code === COUNTRY_CODE.toUpperCase(),
    );
    const currentCountryOptionIndex = (countryOptions ?? []).findIndex(
      (i) => i.value === currentCountry?.code,
    );
    const currentCountryOption = countryOptions[currentCountryOptionIndex];
    if (currentCountryOption) {
      countryOptions.splice(currentCountryOptionIndex, 1);
      countryOptions = [currentCountryOption, ...countryOptions];
    }

    return { countryOptions };
  }, [availableCountries, currentLanguageCode, i18n, t]);

  const [countryCodeValue] = useWatch({
    name: [`${formFieldPrefix}.countryCode`, `${formFieldPrefix}.settlementId`],
    control,
  });

  return (
    <div className="flex flex-col gap-4">
      <p className="text-gray-900 p-0 text-lg font-semibold">
        {t(`KYC.modal.step2.form.title.${formFieldPrefix}`)}
      </p>
      <FormAutocomplete
        name={`${formFieldPrefix}.countryCode`}
        options={countryOptions}
        control={control}
        label={t("KYC.modal.step2.form.fields.countryCode.label")}
        placeholder={t("KYC.modal.step2.form.fields.countryCode.placeholder")}
      />
      {countryCodeValue ? (
        availableCountries?.find((country) => country.code === countryCodeValue)
          ?.freeLine ? (
          <>
            <FormInput
              name={`${formFieldPrefix}.settlement`}
              control={control}
              label={t("KYC.modal.step2.form.fields.settlement.label")}
              placeholder={t(
                "KYC.modal.step2.form.fields.settlement.placeholder",
              )}
            />
            <div className="flex flex-col gap-2 sm:flex-row">
              <FormInput
                name={`${formFieldPrefix}.street`}
                control={control}
                label={t("KYC.modal.step2.form.fields.street.label")}
                placeholder={t(
                  "KYC.modal.step2.form.fields.street.placeholder",
                )}
                className="w-full"
              />
              <div className="flex w-full gap-2">
                <FormInput
                  control={control}
                  name={`${formFieldPrefix}.houseNumber`}
                  label={t("KYC.modal.step2.form.fields.houseNumber.label")}
                  placeholder={t(
                    "KYC.modal.step2.form.fields.houseNumber.placeholder",
                  )}
                />
                <FormInput
                  control={control}
                  name={`${formFieldPrefix}.apartmentNumber`}
                  label={t("KYC.modal.step2.form.fields.apartmentNumber.label")}
                  placeholder={t(
                    "KYC.modal.step2.form.fields.apartmentNumber.placeholder",
                  )}
                />
              </div>
            </div>
          </>
        ) : (
          <FormAddressInput
            control={control}
            municipalityFieldName={`${formFieldPrefix}.municipalityId`}
            settlementFieldName={`${formFieldPrefix}.settlementId`}
            streetFieldName={`${formFieldPrefix}.streetId`}
            houseFieldName={`${formFieldPrefix}.houseNumber`}
            apartmentFieldName={`${formFieldPrefix}.apartmentNumber`}
            resetField={resetField}
            setFocus={setFocus}
            setValue={setValue}
            countryCode={countryCodeValue}
          />
        )
      ) : null}
      {formFieldPrefix === "registrationInformation" && (
        <React.Fragment>
          <FormInput
            iconLeft={LetterIcon}
            name={`${formFieldPrefix}.email`}
            control={control}
            label={t("KYC.modal.step2.form.fields.email.label")}
            placeholder={t("KYC.modal.step2.form.fields.email.placeholder")}
          />
          <FormPhoneInput
            name={`${formFieldPrefix}.phoneNumber`}
            control={control}
            label={t("KYC.modal.step2.form.fields.phoneNumber.label")}
            placeholder={t(
              "KYC.modal.step2.form.fields.phoneNumber.placeholder",
            )}
          />
        </React.Fragment>
      )}
    </div>
  );
};
